import {html, TemplateResult} from "lit";
import {customElement} from "lit/decorators/custom-element.js";
import {property} from "lit/decorators/property.js";
import {when} from 'lit/directives/when.js';
import type {
    RichTextDocument,
    RichTextChangedEvent,
    ImageUploadHandler,
    O2dRichTextEditorElement,
} from "@o2d/rich-text-editor";
import "@o2d/rich-text-editor";
import {ObjectHelper} from "../../helpers/object.helper";
import {AbstractLitElement} from "../abstract-lit.element";
import type {RteValue} from "../../domain/rte-value.type";
import {RteValueHelper} from "../../helpers/rte-value.helper";
import {container} from "tsyringe";
import {BackendClient} from "../../connectivity/backend-client";
import {buildAssetSrc} from "../../helpers/asset-embed.helper";
import {ImageHelper} from "../../helpers/image.helper";
import type {DeltaConversionReport} from "../../helpers/quill-delta-to-rich-text-document.helper";

/**
 * This app's rich-text field editor — `<ha-rte>`, used for `Issue.description`
 * (the issue-edit dialog) and `LinkTile.text` (the Linkview tile dialog).
 *
 * Since the RTE unification (2026-09) this wraps `@o2d/rich-text-editor`'s
 * `<o2d-rich-text-editor>` — the SAME editor engine `knowledge-rte` and the
 * whiteboard textbox use — instead of mounting a standalone Quill instance.
 * The public contract (`value`/`readonly`/`helper-text` properties, the
 * `value-changed` event) is unchanged, so every existing consumer
 * (`issue-dialog-renderer.service.ts`, `ha-dialog-link-tile-edit.component.ts`,
 * the `bindTo`/form-field wiring behind them) keeps working untouched — only
 * what happens INSIDE this element changed.
 *
 * `value` accepts either shape of `RteValue` (`Delta | RichTextDocument`): a
 * legacy Quill Delta — every row not yet re-saved since this migration — is
 * converted lazily on assignment (`RteValueHelper.toRichTextDocument`,
 * `QuillDeltaToRichTextDocumentConverter`); every value this element itself
 * EMITS (`value-changed`) is always a `RichTextDocument`, so a field is a
 * `RichTextDocument` for good the moment it is next saved.
 *
 * The earlier `minimal` toolbar variant (bold + colour + background only, for
 * the tile-text field) is gone — Anke, 2026-09-14: "die Linkview-Kachel darf
 * künftig die volle Toolbar bekommen". Both fields now share the exact same
 * full editor.
 */
@customElement(`ha-rte`)
export class HaRteComponent extends AbstractLitElement {

    @property({type: Boolean})
    public readonly = false;
    @property({type: String, attribute: `helper-text`})
    public helperText: string | null = null;

    private _value: RteValue | null = null;
    private _content: RichTextDocument = RteValueHelper.empty();

    /** The converter's report for the value currently loaded, or null when nothing was converted
     *  (already a document, or empty). Consumed by the first edit; see `_logConversionOnce`. */
    private _pendingConversionReport: DeltaConversionReport | null = null;

    @property({type: Object})
    public get value(): RteValue | null {
        return this._value;
    }

    public set value(value: RteValue | null) {
        // Null after null changes nothing. Null after a text does: the dialog uses one instance for
        // every issue, so switching to an issue without a description must empty the editor.
        if (value == null && this._value == null) {
            return;
        }
        if (value != null && ObjectHelper.deepEqual(value, this._value)) {
            return;
        }

        this._value = value ?? null;
        const {document, report} = RteValueHelper.toRichTextDocumentWithReport(value);
        // Always a new object for the editor: it reloads only on a new reference. Handing back the
        // document it was given before (switching back to an issue) left the text of the issue shown
        // in between on screen. A converted Delta is a new object already.
        this._content = report === null ? RteValueHelper.copy(document) : document;
        // Kept, not logged yet: a conversion nobody edits changes nothing on disk. The entry
        // belongs to the first edit, in `_onChanged` — that is the moment this conversion is
        // about to become the stored value.
        this._pendingConversionReport = report;
        this.requestUpdate();
    }

    public render(): TemplateResult {
        return html`
            <o2d-rich-text-editor
                .mode=${this.readonly ? 'view' : 'edit'}
                .content=${this._content}
                .imageUploadHandler=${this._imageUploadHandler}
                @rich-text-changed=${this._onChanged}
            ></o2d-rich-text-editor>
            ${when(!!this.helperText, () => html`
                <p class="input-helptext">${this.helperText}</p>
            `)}
        `;
    }

    /**
     * Reports a change the editor is still holding back (it waits 300 ms after the last keystroke)
     * right now, to the form field this element is bound to at this moment.
     *
     * Call it before the owner of the text changes or before saving: a switch to another issue
     * rebinds this element, and the held-back change would otherwise land in the next issue's form,
     * while a save would miss the last keystrokes.
     */
    public flushPendingChange(): void {
        this._innerEditor()?.flushChanged();
    }

    /**
     * Drops a picture whose upload is still running, so it is not inserted into the next issue's
     * text. The editor logs the dropped picture; the user can insert it again.
     */
    public abandonPendingInserts(): void {
        this._innerEditor()?.abandonPendingInserts();
    }

    /** Null before the first render. */
    private _innerEditor(): O2dRichTextEditorElement | null {
        return this.querySelector<O2dRichTextEditorElement>('o2d-rich-text-editor');
    }

    private _onChanged(event: RichTextChangedEvent): void {
        const doc = event.detail.doc;
        this._logConversionOnce();
        this._value = doc;
        this._content = doc;
        this.fire(doc);
    }

    /**
     * Writes one line the first time the user edits a description that arrived as a legacy Delta.
     *
     * WHY AT ALL: the one-time migration compares every converted value against its source and
     * refuses those that lose content. This path calls the same converter and dropped its report,
     * so the deliberate losses (a bullet indented under no parent, a colour or a link on an image,
     * a broken `//:0` address) happen here too — uncounted, spread over weeks. Stefan's decision
     * on 2026-09-20: log them, do not surface them in the UI.
     *
     * WHY IT LOGS EVEN WHEN NOTHING WAS LOST: without the empty case, "no entry" means either
     * "converted, lost nothing" or "this path never ran" — the two things the log exists to tell
     * apart. A zero has to be written to count as a zero.
     *
     * WHY ONCE: `_onChanged` fires on every keystroke; the conversion happened once, on load.
     */
    private _logConversionOnce(): void {
        const report = this._pendingConversionReport;
        if (!report) {
            return;
        }
        this._pendingConversionReport = null;
        console.info('rte: legacy Delta converted on edit', {
            skippedOps: report.skippedOps.length,
            unknownListFormats: report.unknownListFormats.length,
            detail: {skippedOps: report.skippedOps, unknownListFormats: report.unknownListFormats},
        });
    }

    /**
     * A picture pasted or picked into a description is downscaled, then UPLOADED, and the document keeps
     * only its address.
     *
     * Without a handler at all, `<o2d-rich-text-editor>` embeds the picture as a raw, uncompressed inline
     * base64 data-URL with no size limit (found in Review-Lauf 03). Until 2026-09-18 this handler closed
     * half of that gap the way the Quill editor had: downscale, re-encode as WebP, and embed the result —
     * smaller, but still the whole picture as text inside the description, which is why single descriptions
     * run to hundreds of kilobytes.
     *
     * Now it uploads instead. The upload path is not new: `POST /api/v1/files` deduplicates by checksum, and
     * `knowledge-whiteboard` has been using the same `BackendClient.uploadFile` for its pasted images. The
     * uid that comes back is a ShortId and `/api/v1/files/<uid>` resolves exactly that spelling, no other
     * (measured and pinned in `dashboard-creator.component.ts`).
     *
     * The downscale stays and happens BEFORE the upload, so the original never leaves the browser: at most
     * 1920 by 1080, fitting inside the box in both dimensions, ratio kept (`ImageHelper.fitInside`). The
     * server rejects anything above that as well, because this is one client among possible ones.
     *
     * Throwing here is deliberate: the editor reports a failed upload to the user, whereas returning a
     * data-URL as a fallback would put back exactly what this change removes, and silently.
     */
    private _imageUploadHandler: ImageUploadHandler = async (file: File): Promise<{ src: string }> => {
        const rawDataUrl = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = () => reject(reader.error ?? new Error('Failed to read image file'));
            reader.readAsDataURL(file);
        });

        const downscaled = await ImageHelper.reducePixelsFn(rawDataUrl);
        // reducePixelsFn always re-encodes as WebP, so the name says what the bytes are.
        const upload = ImageHelper.dataUrlToFile(downscaled, `${file.name.replace(/\.[^.]+$/, '')}.webp`);
        const asset = await container.resolve(BackendClient).uploadFile(upload);

        return {src: buildAssetSrc(asset.uid)};
    };

}
